import React, { useEffect, useState } from "react";
import * as api from "../../../../api/generated-api";
import { Box, List, Typography, useTheme } from "@mui/material";
import { useHistory } from "react-router-dom";
import { ProgressIndicator } from "../../../common/progress/ProgressIndicator";
import PageEmptyStateMessage from "../../../common/PageEmptyStateMessage";
import DocumentSignatureListItemRow from "./DocumentSignatureListItemRow";
import { toValidIdentifier } from "../../../../helpers/utilsTS";
import { DKDialog } from "../../../common/DKDialog";

type YourSignaturesProps = {
  orgId: number;
  dealId: number;
  isSignButtonVisible?: boolean;
  dealDocumentSignatures: api.DealDocumentSignature[];
  selectedDealDocumentSignatureId?: number;
  showSignAllDialog?: boolean;
  onSignAllDealDocuments: () => void;
  onCancelSignAllDealDocuments: () => void;
};

type GroupedDealDocumentSignatures = {
  title: string;
  dealDocumentSignatures: api.DealDocumentSignature[];
};

const YourSignatures = ({
  orgId,
  dealId,
  isSignButtonVisible = false,
  dealDocumentSignatures,
  selectedDealDocumentSignatureId,
  showSignAllDialog = false,
  onSignAllDealDocuments,
  onCancelSignAllDealDocuments,
}: YourSignaturesProps) => {
  const [signingInProgress, setSigningInProgress] = useState<boolean>(false);
  const [groupedDealDocumentSignatures, setGroupedDealDocumentSignatures] =
    useState<GroupedDealDocumentSignatures[]>(null);

  const history = useHistory();

  const theme = useTheme();

  useEffect(() => {
    if (dealDocumentSignatures?.length > 0) {
      const dealDocumentSignaturesRequestedOrSigned =
        dealDocumentSignatures.filter(
          (dealDocumentSignature) =>
            dealDocumentSignature.status === api.SigningStatus.requested ||
            dealDocumentSignature.status === api.SigningStatus.signed
        );

      // define the groups of signatures to show and in what order
      const legalEntityTypesToShow = [
        api.LegalEntityType.Chief_Executive_Officer,
        api.LegalEntityType.Secretary,
        api.LegalEntityType.Key_Common_Holder,
        api.LegalEntityType.Board_Member,
        api.LegalEntityType.Stockholder,
        api.LegalEntityType.Company_Counsel,
        api.LegalEntityType.Investor_Investment,
      ];

      const groupedDealDocumentSignatures: GroupedDealDocumentSignatures[] = [];

      // get the unique legal entity names for the deal document signatures sorted alphabetically
      const dealLegalEntityNamesForDealDocumentSignatures =
        dealDocumentSignaturesRequestedOrSigned
          .filter(
            (dealDocumentSignature) =>
              dealDocumentSignature.dealLegalEntity.legalEntityType ===
              api.LegalEntityType.Investor_Investment
          )
          .map(
            (dealDocumentSignature) =>
              dealDocumentSignature?.dealLegalEntity?.legalEntity?.name
          )
          .sort((a, b) => (a > b ? 1 : -1));

      const uniqueDealLegalEntityNamesForDealDocumentSignatures = new Set(
        dealLegalEntityNamesForDealDocumentSignatures
      );

      legalEntityTypesToShow.forEach((legalEntityType) => {
        if (legalEntityType === api.LegalEntityType.Investor_Investment) {
          // if the legal entity type is Investor_Investment, then we need to group by legal entity name
          uniqueDealLegalEntityNamesForDealDocumentSignatures.forEach(
            (legalEntityName) => {
              const filteredDealDocumentSignatures =
                dealDocumentSignaturesRequestedOrSigned.filter(
                  (dealDocumentSignature) => {
                    return (
                      dealDocumentSignature?.dealLegalEntity?.legalEntity
                        ?.name === legalEntityName
                    );
                  }
                );

              groupedDealDocumentSignatures.push({
                title: legalEntityName,
                dealDocumentSignatures: filteredDealDocumentSignatures,
              });
            }
          );
        } else {
          // if the legal entity type is not Investor_Investment, then we need to group by legal entity type
          const filteredDealDocumentSignatures =
            dealDocumentSignaturesRequestedOrSigned.filter(
              (dealDocumentSignature) => {
                return (
                  dealDocumentSignature.dealLegalEntity.legalEntityType ===
                  legalEntityType
                );
              }
            );

          groupedDealDocumentSignatures.push({
            title:
              legalEntityType === api.LegalEntityType.Company_Counsel
                ? "Company Counsel"
                : legalEntityType,
            dealDocumentSignatures: filteredDealDocumentSignatures,
          });
        }
      });

      //remove groups with no signatures
      const groupedDealDocumentSignaturesWithSignatures =
        groupedDealDocumentSignatures.filter((group) => {
          return group.dealDocumentSignatures.length > 0;
        });

      //update state
      setGroupedDealDocumentSignatures(
        groupedDealDocumentSignaturesWithSignatures
      );
    }
  }, [dealDocumentSignatures]);

  const handleSignDocument = (documentSigner: api.DealDocumentSignature) => {
    history.push(
      `/${orgId}/deals/${dealId}/documents/${documentSigner.dealDocumentId}/sign/${documentSigner.id}`
    );
  };

  const SignaturesGroupedByRole = () => {
    return (
      <Box>
        <Typography mb="25px">
          {`You've been requested to sign the following documents. Signatures are
          grouped by the role for which you are signing.`}
        </Typography>
        {groupedDealDocumentSignatures?.length > 0 &&
          groupedDealDocumentSignatures.map((group, index) => {
            return (
              <Box
                data-testid={`signature-group-${toValidIdentifier(
                  group.title
                )}`}
                key={index}
                mb="10px"
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                  }}
                >
                  Signing as
                </Typography>
                <Typography
                  sx={{
                    fontSize: theme.typography.h3.fontSize,
                    fontWeight: theme.typography.h3.fontWeight,
                  }}
                >
                  {group.title}
                </Typography>
                <List>
                  {group.dealDocumentSignatures.map((dealDocumentSignature) => {
                    return (
                      <DocumentSignatureListItemRow
                        key={dealDocumentSignature.id}
                        orgId={orgId}
                        dealId={dealId}
                        dealDocumentSignature={dealDocumentSignature}
                        isSelected={
                          dealDocumentSignature.id ===
                          selectedDealDocumentSignatureId
                        }
                        isSignButtonVisible={isSignButtonVisible}
                        onSignDocumentClick={(dealDocumentSignature) => {
                          handleSignDocument(dealDocumentSignature);
                        }}
                      />
                    );
                  })}
                </List>
              </Box>
            );
          })}
      </Box>
    );
  };

  const SignAllDialog = () => {
    if (signingInProgress) {
      return (
        <DKDialog
          open={showSignAllDialog}
          title="Sign Documents"
          contentText="Documents are being signed..."
        >
          <ProgressIndicator size={100} />
        </DKDialog>
      );
    } else {
      return (
        <DKDialog
          open={showSignAllDialog}
          title="Sign Documents"
          contentText="You are about to sign all of the following documents:"
          actionButtons={[
            {
              dataTestId: "cancel-sign-all-documents-button",
              onClick: () => {
                onCancelSignAllDealDocuments();
              },
              label: "Cancel",
            },
            {
              primary: true,
              dataTestId: "confirm-sign-all-documents-button",
              onClick: () => {
                setSigningInProgress(true);
                onSignAllDealDocuments();
              },
              label: "Sign All",
            },
          ]}
        >
          <SignaturesGroupedByRole />
        </DKDialog>
      );
    }
  };

  return (
    <Box>
      {!dealDocumentSignatures ||
        (dealDocumentSignatures?.length === 0 && (
          <PageEmptyStateMessage
            text="This will contain a list of documents for you to sign once your
              signature is requested."
          />
        ))}
      {dealDocumentSignatures && dealDocumentSignatures.length > 0 && (
        <SignaturesGroupedByRole />
      )}
      <SignAllDialog />
    </Box>
  );
};

export default YourSignatures;
